import {
  loginRequest,
  loginSuccess,
  loginFailure,
  loginNotMade,
} from "../actions";
import { useDispatch } from "react-redux";
import { OIDCState, OIDCLoginParam } from "../types/openId";
import { useTranslation } from "react-i18next";
import { useCookies } from "@/hooks/useCookies";
import { default as openidService } from "../services/openId";
import { createSearchParams, useNavigate, useSearchParams } from "react-router";
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";
import useLangs from "@/languages/hooks/useLangs";
import { generateAndSignKey, jwtVerifyState } from "../utils/openId";
import { defineToken, undefineToken } from "../utils/token";
import { generateHash, randomString } from "@/utils/misc";
import useContractation from "@/components/Contractation/hooks/useContractation";

const useOpenId = () => {
  const Settings = useSettingsContext();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { firstContractationPath } = useContractation();
  const {
    getOpenIdNonce,
    setOpenIdNonce,
    removeOpenIdNonce,
    getOpenIdKey,
    setOpenIdKey,
    removeOpenIdKey,
    getOpenIdToken,
    setOpenIdToken,
    removeOpenIdToken,
  } = useCookies();
  
  const { currentLang, changeLang, getBCP47Lang } = useLangs();

  const removeSearchParams = () => {
    searchParams.delete("code");
    searchParams.delete("state");

    // First solution I've come up with to solve the redux not registering
    // as authenticating between rerenders
    // The current route must be /connect or this won't work
    searchParams.set("waiting", "true");

    setSearchParams(searchParams);
  }

  const redirectOpenId = (currentPath: string, searchParams: URLSearchParams) => {
    const oidc = Settings.oidc;
    const authorize = Settings.oidc?.endpoints.authorize;
    if (oidc && authorize) {

      // Redirect to the OpenID provider to register new user or login to get an access code.
      let extraParams: {[key: string]: string | boolean | undefined} = {};
      let statePayload: OIDCState = {"qs": {}};
      if (authorize?.extraParamsVars && 'lang' in authorize.extraParamsVars) {
        extraParams[authorize?.extraParamsVars?.lang] = currentLang;
      }
      if (authorize?.extraParams) {
        extraParams = { ...authorize.extraParams };
      }
      const stringifiedExtraParams = Object.entries(extraParams).map(([key, val]) => `${key}=${val}`).join('&');
      
      statePayload["qs"]['redirectPath'] = currentPath;

      if (searchParams) {
        statePayload["qs"]['searchParams'] = searchParams.toString();
      }

      // Nonce
      const nonce  = generateHash(randomString());
      setOpenIdNonce(nonce);

      // OpenId Key
      generateAndSignKey(statePayload).then(({
        key, jwt
      }) => {
        setOpenIdKey(key);
        window.location.href = `${oidc.providerUrl}${authorize.resource}?client_id=${oidc.clientId}&response_type=${authorize.response_type}&scope=${authorize.scope}&state=${jwt}&nonce=${nonce}&redirect_uri=${authorize.redirectUrl}&${stringifiedExtraParams}`;
      })
    } else {
      // Throw error oidc not well defined
    }
  };

  const loginOpenId = ({
    code,
    state
  }: OIDCLoginParam) => {
    dispatch(loginRequest('OPEN_ID'));
    const nonce = getOpenIdNonce();
    if (!nonce) {
      dispatch(loginFailure({statusCode: 504, status: "error"}));
      console.debug(`Use to debug: http://localhost:5173/connect?code=${code}&state=${state}`);
      return;
    }
    removeSearchParams();
    const key = getOpenIdKey();
    jwtVerifyState(state, key ?? '')
    .then(({payload}) => {
      removeOpenIdKey();
      openidService.login({code, nonce})
      .then(res => {
        const redirect = payload.qs.redirectPath;
        delete payload.qs.redirectPath;
        const appendSearchParams = createSearchParams(payload.qs.searchParams);

        const { locale, new_customer: newCustomer } = defineToken(res.token);
        if (locale) {
          changeLang(getBCP47Lang(locale));
        }

        setOpenIdToken(res.openid_token);

        dispatch(loginSuccess({newCustomer}));

        if ((newCustomer || appendSearchParams?.get("promocode")) && firstContractationPath) {
          navigate({
            pathname: t(firstContractationPath),
            search: appendSearchParams.toString()
          });
        } else if (redirect) {
          navigate({
            pathname: t(redirect),
            search: appendSearchParams.toString()
          });
        } else {
          navigate({
            pathname: t(`common:url.${Settings.homepage ?? "invoices"}`),
            search: appendSearchParams.toString()
          });
        }
      })
      .catch(err => {
        undefineToken();
        removeOpenIdNonce();
        removeOpenIdToken();
        dispatch(loginFailure({
          statusCode: 401,
          status: "error",
        }));
      });
    })
    .catch(err => {
      undefineToken();
      removeOpenIdNonce();
      removeOpenIdToken();
      dispatch(loginFailure({
        statusCode: 401,
        status: "error",
      }));
    });
  };

  const logoutOpenId = (openIdToken: string) => {
    if (Settings.features.oidc && Settings.oidc) {
      const providerUrl = Settings.oidc.providerUrl;
      const logout = Settings.oidc.endpoints.logout;
      const redirectUrl = `${providerUrl}${logout.resource}?${logout.params.token.name}=${openIdToken}&${logout.params.redirectUrl.name}=${logout.params.redirectUrl.value}`;
      window.location.href = redirectUrl;
    }
  }

  return {
    redirectOpenId,
    loginOpenId,
    logoutOpenId
  }
};

export default useOpenId;
